import React, { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { statusColors } from "../../styles/palette";
import { StatusDistribution } from "../../api/dashboard";

type Props = {
  data: StatusDistribution[];
  centerLabel?: string;
};

const FALLBACK = "#64748B";

export const FleetStatusDonut: React.FC<Props> = ({ data, centerLabel = "vehicles" }) => {
  const theme = useTheme();
  const total = useMemo(() => data.reduce((s, d) => s + d.count, 0), [data]);

  const chartData = data.map((d) => ({
    name: d.label,
    value: d.count,
    color: statusColors[d.status] ?? FALLBACK,
  }));

  return (
    <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" spacing={2} sx={{ height: "100%" }}>
      <Box sx={{ position: "relative", width: 200, height: 200, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={62}
              outerRadius={92}
              paddingAngle={total > 0 ? 3 : 0}
              cornerRadius={6}
              stroke="none"
            >
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 10,
                fontSize: 12,
                color: theme.palette.text.primary,
              }}
              itemStyle={{ color: theme.palette.text.primary }}
            />
          </PieChart>
        </ResponsiveContainer>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            pointerEvents: "none",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: 32, fontWeight: 800, lineHeight: 1, color: "text.primary" }}>
              {total}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{centerLabel}</Typography>
          </Box>
        </Box>
      </Box>

      <Stack spacing={1.25} sx={{ flex: 1, width: "100%" }}>
        {chartData.map((entry) => {
          const pct = total > 0 ? Math.round((entry.value / total) * 100) : 0;
          return (
            <Stack key={entry.name} direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: entry.color }} />
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{entry.name}</Typography>
              </Stack>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>{pct}%</Typography>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};
